/**
 * ageCopy.js — age-aware child-facing copy over ONE feature set.
 * Same feature, different developmental presentation (spec §7).
 */
import { useProfile, getActiveProfile } from "@/lib/profileStore";
import { useAgeGroup, getAgeGroup, getAgeMeta } from "@/lib/ageGroup";

/** ONE tier answer: active child's age wins; else the legacy age-group chip; else profile default. */
export function getUnifiedTier() {
  try {
    const p = getActiveProfile();
    if (p.hasChild) return p.tier;
    return getAgeMeta(getAgeGroup())?.tier || p.tier || "explorers";
  } catch { return "explorers"; }
}
export function useUnifiedTier() {
  const p = useProfile();
  const [, , meta] = useAgeGroup();
  return p.hasChild ? p.tier : (meta?.tier || p.tier || "explorers");
}

const COPY = {
  welcomeNew:     { seedlings: "Hello, Explorer!", explorers: "Welcome, Explorer!", pioneers: "Welcome to My World Quest" },
  welcomeBack:    { seedlings: "You're back!", explorers: "Welcome back", pioneers: "Welcome back" },
  startFirst:     { seedlings: "Let's Go!", explorers: "Start your first quest", pioneers: "Start your first quest" },
  continue:       { seedlings: "Keep going!", explorers: "Continue", pioneers: "Continue" },
  nextQuest:      { seedlings: "Where should we go?", explorers: "Choose your next quest", pioneers: "Continue your learning" },
  exploreWorld:   { seedlings: "Where should we explore?", explorers: "Choose your next destination", pioneers: "Explore countries, cultures and places" },
  practise:       { seedlings: "Try it!", explorers: "Practise this skill", pioneers: "Practise" },
  challenge:      { seedlings: "A bigger one!", explorers: "Try a challenge", pioneers: "Choose a challenge" },
  progress:       { seedlings: "Look what you did!", explorers: "Your adventure so far", pioneers: "Your progress" },
  skills:         { seedlings: "Things you can do", explorers: "Skills you're growing", pioneers: "Build your skills" },
  collections:    { seedlings: "My treasures", explorers: "My collections", pioneers: "Collections" },
  stillToFind:    { seedlings: "Still to find", explorers: "Still to discover", pioneers: "Not yet discovered" },
  newTopic:       { seedlings: "Something new!", explorers: "Discover something new", pioneers: "Explore a new topic" },
  complete:       { seedlings: "All done!", explorers: "Quest complete", pioneers: "Complete" },
  discoverMore:   { seedlings: "Find more!", explorers: "Keep exploring", pioneers: "Explore more" },
};

export function ageCopy(key, tier = "explorers") {
  const c = COPY[key];
  if (!c) return "";
  return c[tier] || c.explorers;
}

export function useAgeCopy() {
  const tier = useUnifiedTier();
  return (key) => ageCopy(key, tier);
}

/** Density + intensity knobs by tier — consumed by surfaces that branch on age. */
export const TIER_PRESENTATION = {
  seedlings: { emoji: true, mascotScale: 1.3, showTechnicalMeta: false, maxOptions: 3, celebration: "large", cardGap: "gap-4" },
  explorers: { emoji: true, mascotScale: 1.0, showTechnicalMeta: false, maxOptions: 4, celebration: "medium", cardGap: "gap-3" },
  pioneers:  { emoji: false, mascotScale: 0.7, showTechnicalMeta: true, maxOptions: 6, celebration: "subtle", cardGap: "gap-3" },
};
export function useTierPresentation() {
  const tier = useUnifiedTier();
  return { tier, ...(TIER_PRESENTATION[tier] || TIER_PRESENTATION.explorers) };
}
